import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "./AnimatePresenceWrapper";
import { Menu as MantineMenu } from "@mantine/core";
import { LogIn, User, Menu, X, LogOut, Ticket, CreditCard } from "lucide-react";
import { useUser } from "../context/UserContext";
import { ProfileModal } from "./ProfileModal";
import {
  Avatar as CustomAvatar,
  AvatarImage,
  AvatarFallback,
} from "./ui/avatar";
import { useNavigate } from "react-router-dom";
import { getMyTicketsUrl, getPaymentHistoryUrl } from "../lib/utils/domain";

interface NavbarProps {
  organizationName: string;
  logoImage: string;
  onLoginClick: () => void;
}

export function Navbar({
  organizationName,
  logoImage,
  onLoginClick,
}: NavbarProps) {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated, clearUserProfile } = useUser();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    // Get organization slug from user profile or URL
    const orgSlug = userProfile?.organizationSlug;
    clearUserProfile(orgSlug);
    window.location.reload();
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo Section */}
          <motion.div className="flex items-center gap-4">
            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-[#17265a] to-[#2d3f7a] p-0.5">
              <div className="w-full h-full rounded-lg overflow-hidden bg-white">
                <img
                  src={logoImage}
                  alt={`${organizationName} logo`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-[#17265a] text-xl">{organizationName}</h1>
            </div>
          </motion.div>

          {/* Desktop Login Button / User Menu */}
          <div className="hidden md:block">
            {isAuthenticated && userProfile ? (
              <MantineMenu shadow="md" width={200} position="bottom-end">
                <MantineMenu.Target>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <CustomAvatar className="w-10 h-10">
                      {userProfile.user.image ? (
                        <AvatarImage
                          src={userProfile.user.image}
                          alt={`${userProfile.user.first_name} ${userProfile.user.last_name}`}
                        />
                      ) : null}
                      <AvatarFallback className="bg-[#17265a] text-white text-sm font-semibold">
                        {getInitials(
                          userProfile.user.first_name,
                          userProfile.user.last_name
                        )}
                      </AvatarFallback>
                    </CustomAvatar>
                  </motion.button>
                </MantineMenu.Target>

                <MantineMenu.Dropdown>
                  <MantineMenu.Label>
                    {userProfile.user.first_name} {userProfile.user.last_name}
                  </MantineMenu.Label>
                  <MantineMenu.Item
                    leftSection={<User className="w-4 h-4" />}
                    onClick={() => setShowProfileModal(true)}
                  >
                    Profile
                  </MantineMenu.Item>
                  <MantineMenu.Item
                    leftSection={<Ticket className="w-4 h-4" />}
                    onClick={() =>
                      navigate(getMyTicketsUrl(userProfile.organizationSlug))
                    }
                  >
                    My Tickets
                  </MantineMenu.Item>
                  <MantineMenu.Item
                    leftSection={<CreditCard className="w-4 h-4" />}
                    onClick={() =>
                      navigate(
                        getPaymentHistoryUrl(userProfile.organizationSlug)
                      )
                    }
                  >
                    Payment History
                  </MantineMenu.Item>
                  <MantineMenu.Divider />
                  <MantineMenu.Item
                    color="red"
                    leftSection={<LogOut className="w-4 h-4" />}
                    onClick={handleLogout}
                  >
                    Logout
                  </MantineMenu.Item>
                </MantineMenu.Dropdown>
              </MantineMenu>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onLoginClick}
                className="group relative bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-white/10"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5 }}
                />
                <LogIn className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Login</span>
              </motion.button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#17265a] hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-4 py-4 space-y-3">
              {isAuthenticated && userProfile ? (
                <>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <CustomAvatar className="w-10 h-10">
                      {userProfile.user.image ? (
                        <AvatarImage
                          src={userProfile.user.image}
                          alt={`${userProfile.user.first_name} ${userProfile.user.last_name}`}
                        />
                      ) : null}
                      <AvatarFallback className="bg-[#17265a] text-white text-sm font-semibold">
                        {getInitials(
                          userProfile.user.first_name,
                          userProfile.user.last_name
                        )}
                      </AvatarFallback>
                    </CustomAvatar>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {userProfile.user.first_name}{" "}
                        {userProfile.user.last_name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {userProfile.user.email}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowProfileModal(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <User className="w-5 h-5" />
                    <span>Profile</span>
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      navigate(getMyTicketsUrl(userProfile?.organizationSlug));
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <Ticket className="w-5 h-5" />
                    <span>My Tickets</span>
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      navigate(
                        getPaymentHistoryUrl(userProfile?.organizationSlug)
                      );
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <CreditCard className="w-5 h-5" />
                    <span>Payment History</span>
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </motion.button>
                </>
              ) : (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onLoginClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center justify-center gap-2 shadow-md"
                >
                  <LogIn className="w-5 h-5" />
                  <span>Login</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <AnimatePresence>
        {showProfileModal && userProfile && (
          <ProfileModal
            onClose={() => setShowProfileModal(false)}
            user={userProfile.user}
            organization={userProfile.organization}
          />
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
